import React, { useState, useEffect, useRef } from 'react';
import { Alert, PageHeader, ListPage } from '@/components/ui';
import { Asset } from '../../../types/Asset';
import { assetService, PaginatedAssetsResponse, AssetQueryParams, AssetFilterOptions } from '../../../services/assetService';
import { usePermissions } from '../../../hooks/usePermissions';
import AssetsTable from '../components/AssetsTable';
import AssetModal from '../components/AssetModal';
import AssetFilters, { AssetFilterValues, EMPTY_ASSET_FILTERS } from '../components/AssetFilters';
import AssetHistoryModal from '../components/AssetHistoryModal';
import ServerPagination from '../../../components/ServerPagination';
import { confirmAction } from '@/components/ui';
import { usePersistedListState } from '../../../hooks/usePersistedListState';

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;
/** sessionStorage page key for this page's remembered list state (V2-024). */
const LIST_KEY = 'assets';

const AssetsPage: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [historyAsset, setHistoryAsset] = useState<Asset | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Applied filters (what's actually sent to server), the search, sort and page are kept for
  // the sign-in (V2-024), so leaving the page and coming back - or Back - restores them.
  const [filters, setFilters] = usePersistedListState<AssetFilterValues>(LIST_KEY, 'filters', EMPTY_ASSET_FILTERS);

  // Search: always visible in the filter panel's header bar (V2-077); runs on Enter.
  const [searchTerm, setSearchTerm] = usePersistedListState(LIST_KEY, 'search', '');
  const [searchInput, setSearchInput] = useState(searchTerm);

  const [savedPage, setSavedPage] = usePersistedListState(LIST_KEY, 'page', {
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
  });
  const [pagination, setPagination] = useState({
    page: savedPage.page >= 1 ? savedPage.page : DEFAULT_PAGE,
    limit: PAGE_SIZE_OPTIONS.includes(savedPage.limit) ? savedPage.limit : DEFAULT_LIMIT,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  useEffect(() => {
    setSavedPage({ page: pagination.page, limit: pagination.limit });
  }, [pagination.page, pagination.limit]);

  // Server-side sort (allowlisted server-side)
  const [sort, setSort] = usePersistedListState<{ by: string | null; dir: 'asc' | 'desc' }>(LIST_KEY, 'sort', {
    by: null,
    dir: 'asc',
  });
  const sortBy = sort.by;
  const sortDir = sort.dir;

  // Add filter options state for server-side filtering
  const [filterOptions, setFilterOptions] = useState<AssetFilterOptions>({
    serviceAreas: [],
    contractAreas: [],
    assetTypes: []
  });

  const { hasPermission } = usePermissions();

  const fetchAssets = async (resetPage: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use current pagination page or reset to 1
      const currentPage = resetPage ? 1 : pagination.page;
      
      // Build query parameters
      const queryParams: AssetQueryParams = {
        page: currentPage,
        limit: pagination.limit,
        ...(filters.serviceAreaId && { serviceAreaId: parseInt(filters.serviceAreaId) }),
        ...(filters.contractAreaId && { contractAreaId: parseInt(filters.contractAreaId) }),
        ...(filters.assetType && { assetType: filters.assetType }),
        ...(searchTerm && { search: searchTerm }),
        // V3-026: inactive assets only with Display Inactive on.
        ...(filters.displayInactive && { includeInactive: true }),
        ...(sortBy && { sortBy, sortOrder: sortDir })
      };

      console.log('AssetsPage: Fetching assets with params:', queryParams);

      const response = await assetService.getAssetsPaginated(queryParams);
      console.log('AssetsPage: Successfully fetched assets:', response.data?.length || 0, 'items');
      
      setAssets(response.data);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNextPage: response.pagination.hasNextPage,
        hasPreviousPage: response.pagination.hasPreviousPage
      });
      
      // If we reset the page, update the pagination page
      if (resetPage) {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    } catch (error) {
      console.error('AssetsPage: Error loading assets:', error);
      setError(error instanceof Error ? error.message : 'Failed to load assets. Please try again later.');
      setAssets([]);
      setPagination(prev => ({ ...prev, totalCount: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      console.log('AssetsPage: Fetching filter options...');
      const options = await assetService.getFilterOptions();
      console.log('AssetsPage: Successfully fetched filter options:', options);
      setFilterOptions(options);
    } catch (error) {
      console.error('AssetsPage: Error fetching filter options:', error);
    }
  };

  // Add pagination handlers
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination(prev => ({
      ...prev,
      limit: newPageSize,
      page: 1
    }));
  };

  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    setSort({ by: key, dir });
  };

  // Enter (or the clear "x") in the header-bar search field: search within the filters.
  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchInput(trimmed);
    setSearchTerm(trimmed);
  };

  // One fetch per change. A filter, search or sort change goes back to page 1; arriving on the
  // page (or coming back to it) fetches the remembered page.
  const queryKey = JSON.stringify([filters.serviceAreaId, filters.contractAreaId, filters.assetType, Boolean(filters.displayInactive), searchTerm, sortBy, sortDir]);
  const lastQueryKey = useRef<string | null>(null);
  const pageKey = `${pagination.page}:${pagination.limit}`;
  const lastPageKey = useRef<string | null>(null);
  useEffect(() => {
    const first = lastQueryKey.current === null;
    if (lastQueryKey.current !== queryKey) {
      lastQueryKey.current = queryKey;
      // A reset lands on page 1, which must not fetch a second time.
      lastPageKey.current = first ? pageKey : `1:${pagination.limit}`;
      fetchAssets(!first);
      return;
    }
    if (lastPageKey.current !== pageKey) {
      lastPageKey.current = pageKey;
      fetchAssets();
    }
  }, [queryKey, pageKey]);

  // Initial data loading
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const handleAddAsset = () => {
    setSelectedAsset(null);
    setShowModal(true);
  };

  const handleRowClick = (asset: Asset) => {
    setSelectedAsset(asset);
    setShowModal(true);
  };

  const handleShowHistory = (asset: Asset) => {
    setHistoryAsset(asset);
  };

  const handleSaveAsset = async (assetData: any) => {
    try {
      if (selectedAsset) {
        // Update existing asset
        await assetService.updateAsset(selectedAsset.id, assetData);
      } else {
        // Create new asset
        await assetService.createAsset(assetData);
      }
      await fetchAssets();
      setShowModal(false);
    } catch (err) {
      console.error('Error saving asset:', err);
      // You might want to show an error message to the user here
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if ((await confirmAction({ title: 'Delete Asset?', confirmLabel: 'Delete', destructive: true }))) {
      try {
        await assetService.deleteAsset(assetId);
        await fetchAssets();
        setShowModal(false);
      } catch (err) {
        console.error('Error deleting asset:', err);
        // You might want to show an error message to the user here
      }
    }
  };

  const handleToggleStatus = async (assetId: number) => {
    try {
      await assetService.toggleAssetStatus(assetId);
      await fetchAssets();
    } catch (err) {
      console.error('Error toggling asset status:', err);
      // You might want to show an error message to the user here
    }
  };



  const handleApplyFilters = (newFilters: AssetFilterValues) => {
    setFilters(newFilters);
  };

  // Clear All: the defaults (no filters, no search), from page 1.
  const handleClearFilters = () => {
    setFilters(EMPTY_ASSET_FILTERS);
    setSearchTerm('');
    setSearchInput('');
  };

  return (
    <ListPage className="assets-page">
      <PageHeader
        title="Assets"
        actions={[
          {
            key: 'add',
            label: 'Add Asset',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !hasPermission('asset:create'),
            onClick: handleAddAsset,
          },
        ]}
      />

      <AssetFilters
        filterOptions={filterOptions}
        filters={filters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        searchApplied={searchTerm !== ''}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: handleSearch,
          placeholder: 'Asset name, asset number, or description',
          label: 'Search assets',
        }}
      />

      {error && (
        <Alert variant="danger" className="tw-mb-3">
          {error}
        </Alert>
      )}

      <ListPage.Body>
        <AssetsTable
          assets={assets}
          loading={loading}
          onRowClick={handleRowClick}
          onShowHistory={handleShowHistory}
          onDelete={hasPermission('asset:delete') ? handleDeleteAsset : undefined}
          onToggleStatus={hasPermission('asset:update') ? handleToggleStatus : undefined}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
        />
      </ListPage.Body>

      <ServerPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        className="tw-mt-2"
      />

      <AssetModal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        asset={selectedAsset}
        onSave={handleSaveAsset}
        filterOptions={filterOptions}
      />

      {historyAsset && (
        <AssetHistoryModal
          visible={!!historyAsset}
          onClose={() => setHistoryAsset(null)}
          assetId={historyAsset.id}
          assetName={historyAsset.name}
        />
      )}
    </ListPage>
  );
};

export default AssetsPage;